import { Injectable } from '@angular/core';
import { deepCopy } from 'na-lib';
import { SchemaNode } from '../service/excel-import.service';

export interface FlattenedSchemaNode extends SchemaNode {
  path: string;
  level: number;
  indexPath: number[];
}

@Injectable({ providedIn: 'root' })
export class SchemaEditorService {
  restrictPublicAccess(nodes: SchemaNode[]): void {
    for (const node of nodes || []) {
      if (node.access && Object.prototype.hasOwnProperty.call(node.access, 'public')) {
        node.access['public'] = Array.isArray(node.access['public']) && node.access['public'].includes('R') ? ['R'] : [];
      }
      if (node.properties) this.restrictPublicAccess(node.properties);
      if (node.items) this.restrictPublicAccess([node.items]);
    }
  }

  countFields(nodes: SchemaNode[]): number {
    return (nodes || []).reduce((count, node) => count + 1 + this.countFields(this.getChildren(node)), 0);
  }

  flatten(nodes: SchemaNode[], parentPath = '', level = 0, parentIndexPath: number[] = []): FlattenedSchemaNode[] {
    return (nodes || []).flatMap((node, index) => {
      const path = parentPath ? `${parentPath}.${node.key}` : node.key;
      const indexPath = [...parentIndexPath, index];
      return [
        { ...node, path, level, indexPath },
        ...this.flatten(this.getChildren(node), path, level + 1, indexPath)
      ];
    });
  }

  getNodeAtPath(nodes: SchemaNode[], path: number[]): SchemaNode | undefined {
    let currentNodes = nodes || [];
    let currentNode: SchemaNode | undefined;
    for (const index of path || []) {
      currentNode = currentNodes[index];
      if (!currentNode) return undefined;
      currentNodes = this.getChildren(currentNode);
    }
    return currentNode;
  }

  findNodePath(nodes: SchemaNode[], targetNode: SchemaNode, parentPath: number[] = []): number[] | undefined {
    for (let index = 0; index < (nodes || []).length; index++) {
      const node = nodes[index];
      const path = [...parentPath, index];
      if (node === targetNode) return path;
      const childPath = this.findNodePath(this.getChildren(node), targetNode, path);
      if (childPath) return childPath;
    }
    return undefined;
  }

  removeNodeAtPath(nodes: SchemaNode[], path: number[]): void {
    const [index, ...rest] = path || [];
    if (index === undefined || !nodes?.[index]) return;
    if (!rest.length) {
      nodes.splice(index, 1);
      return;
    }
    this.removeNodeAtPath(this.getChildren(nodes[index]), rest);
  }

  normalizeNodes(nodes: SchemaNode[]): SchemaNode[] {
    return deepCopy(nodes || []).map(node => {
      const normalized: SchemaNode = { ...node, type: node.type || 'string' };
      if (normalized.type === 'array') {
        const itemNode = normalized.items || { key: 'items', type: 'string' as const };
        normalized.items = {
          ...itemNode,
          key: itemNode.key || 'items',
          type: itemNode.type || 'string'
        };
        if (itemNode.properties?.length) {
          normalized.items.properties = this.normalizeNodes(itemNode.properties);
        }
        delete normalized.properties;
      } else if (normalized.type === 'object') {
        normalized.properties = this.normalizeNodes(normalized.properties || []);
        delete normalized.items;
      } else {
        delete normalized.items;
        delete normalized.properties;
      }
      return normalized;
    });
  }

  createEmptyPayload(nodes: SchemaNode[]): Record<string, unknown> {
    return (nodes || []).reduce<Record<string, unknown>>((payload, node) => {
      if (!node?.key) return payload;
      if (node.type === 'object') payload[node.key] = this.createEmptyPayload(node.properties || []);
      else if (node.type === 'array') payload[node.key] = [];
      else payload[node.key] = null;
      return payload;
    }, {});
  }

  private getChildren(node: SchemaNode): SchemaNode[] {
    return node.type === 'array' ? node.items?.properties || [] : node.properties || [];
  }
}
